import styled from "styled-components";
import { UilSearch, UilTimes } from "@iconscout/react-unicons";
import { useEffect, useRef, useState } from "react";

const Container = styled.button<{ $variant: TVariants }>`
  position: relative;
  width: 280px;
  height: max-content;
  padding: ${({ $variant }) =>
    $variant === "small" ? `9px 12px 9px 24px` : `14px 12px 14px 12px`};
  border-radius: 8px;
  border: 1px solid var(--color-dark-blue-300);
  background: var(--color-light-0);
  cursor: text;

  &:hover {
    background: var(--color-light-0);
  }

  .search-btn {
    position: absolute;
    ${({ $variant }) =>
      $variant !== "small" &&
      `
          right: 12px;
          top: 0;
          display: flex;
          justify-content: flex-end;
          align-items: center;
          border-radius: 0 7px 7px 0;
          width: 20px;
          height: 100%;
          font-size: 18px;
          color: var(--color-main-blue);
          padding-right: 8px;
          background: var(--color-light-0);
        `}
    background: none;
    border: none;
    padding: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .clear {
    position: absolute;
    ${({ $variant }) =>
      $variant === "small"
        ? `top: 9px; right: 12px;`
        : `top: 15px; right: 35px;`}
    color: var(--color-text-labels);
    width: 18px;
    height: 18px;
    font-size: 14px;
    cursor: pointer;
    outline: none;
  }

  ${({ $variant }) =>
    $variant === "small" &&
    ` &::before {
        content: "";
        position: absolute;
        font-family: "unicons";

        color: var(--color-text-labels);
        left: 8px;
        top: 11px;
        width: 18px;
        height: 18px;
        font-size: 14px;
      }
   `}

  &:focus-within {
    border: 1px solid var(--color-main-blue);

    &::before {
      color: var(--color-text-paragraph);
    }
  }

  .clear:focus {
    border: 1px solid var(--color-main-blue);
  }
`;

const Input = styled.input<{ $variant: TVariants }>`
  ${({ $variant }) =>
    $variant === "small"
      ? `width: calc(100% - 30px); margin-right: 20px; margin-left: 10px;`
      : `width: calc(100% - 46px); margin-right: 46px; margin-left: 0;`}
  border: 0;
  color: var(--color-text-paragraph);

  &,
  &::placeholder {
    font-size: 14px;
  }

  &:focus {
    outline: 0;
  }

  &::placeholder {
    color: var(--color-text-labels);
  }
`;

type TVariants = "default" | "small";

type TSearchProps = {
  variant: TVariants;
  id?: string;
  placeholder?: string;
  onChange?: (searchPhrase: string) => void;
  onSearch?: (searchPhrase: string) => void;
  timeoutAmount?: number;
  onClear?: () => void;
  $ref?: (
    inputRef: React.MutableRefObject<HTMLInputElement | null>,
    setSearchString: React.Dispatch<React.SetStateAction<string>>
  ) => void;
};

const Search: React.FC<TSearchProps> = ({
  variant,
  id,
  placeholder,
  onChange,
  onSearch,
  timeoutAmount,
  onClear,
  $ref,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchString, setSearchString] = useState("");

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTimeout(() => {
      setSearchString(e.target.value);
      if (onChange) onChange(e.target.value);
    }, timeoutAmount ?? 50);
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setTimeout(() => {
      if (onSearch && e.key.toLowerCase() === "enter") {
        onSearch(searchString);
      }
    }, timeoutAmount ?? 50);
  };

  const handleOnSearch = () => {
    setTimeout(() => {
      if (onSearch) {
        onSearch(searchString);
      }
    }, timeoutAmount ?? 50);
  };

  const handleOnClear = () => {
    if (inputRef.current !== null) {
      inputRef.current.value = "";

      setTimeout(() => {
        setSearchString("");
        if (onChange) onChange("");
        if (onClear) onClear();
      }, timeoutAmount ?? 50);
    }
  };

  const handleFocus = () => {
    inputRef.current?.focus();
  };

  useEffect(() => {
    if ($ref && inputRef) $ref(inputRef, setSearchString);
  }, []);

  const handleRenderSearch = () => (
    <Container
      $variant={variant}
      id={id && id}
      role="search"
      onClick={handleFocus}
    >
      {variant !== "small" && (
        <button
          type="button"
          className="search-btn"
          tabIndex={-1}
          aria-label="Buscar"
          onClick={handleOnSearch}
        >
          <UilSearch size={18} />
        </button>
      )}
      <Input
        $variant={variant}
        onChange={handleOnChange}
        ref={inputRef}
        type="text"
        placeholder={placeholder ?? "placeholder"}
        onKeyDown={handleOnKeyDown}
      />
      {searchString.length > 0 && (
        <UilTimes
          onClick={handleOnClear}
          tabIndex={0}
          role="button"
          className="clear"
        />
      )}
    </Container>
  );

  return handleRenderSearch();
};

export default Search;
